import React from 'react';
import { useTheme } from '../hooks/useTheme';

/**
 * Standard "loading…" indicator shown across the app while a tab or page
 * is fetching its initial data. Centered spinner + small label.
 *
 * Apply at the head of any conditional render that would otherwise flash
 * an empty state before the first fetch resolves. Pattern:
 *
 *   if (loading && data.length === 0) return <LoadingState />;
 *   if (data.length === 0) return <EmptyState ... />;
 *   return <RealContent />;
 *
 * For the tab/page's loading flag to cover that very first render before
 * the fetch even fires, initialize `useState(true)` (not false) so the
 * spinner is the default render.
 *
 * The spin keyframe is defined globally in App.css (line ~1288).
 *
 * Props:
 *   message  Text shown next to the spinner. Default "Loading…".
 *   padding  CSS padding applied to the wrapper. Default "60px 0" matches
 *            GanttTab's existing loader so the spinner sits comfortably
 *            below tab headers. Override for tighter contexts.
 *   inline   When true, drops the vertical padding so the spinner can sit
 *            inline within a row or a button. Default false.
 */
export default function LoadingState({
  message = 'Loading…',
  padding,
  inline = false,
}) {
  const t = useTheme();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        padding: padding ?? (inline ? '0' : '60px 0'),
        color: t.textMuted,
        fontSize: '15.5px',
      }}
    >
      <div
        style={{
          width: '20px',
          height: '20px',
          border: `3px solid ${t.cardBorder}`,
          borderTopColor: '#2563eb',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          flexShrink: 0,
        }}
      />
      {message ? <span>{message}</span> : null}
    </div>
  );
}
